import { defineStore } from 'pinia';
import { ref } from 'vue';

export type AiPageContentLocateOptions = {
  paragraphIndex?: number;
  paragraphLabel?: string;
};

export type AiPageContentApplyArgs = {
  fieldPath: string;
  original: string;
  revised: string;
  /** 表格等场景下唯一定位单元格 */
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
  /** 用户引用时的原文，用于定位回退 */
  quoted?: string;
};

type ApplyHandler = (args: AiPageContentApplyArgs) => boolean;
type LocateHandler = (quoted: string, fieldPath: string, options?: AiPageContentLocateOptions) => boolean;

function resolveHandlerKey(fieldPath: string, handlers: Map<string, unknown>): string | null {
  if (handlers.has(fieldPath)) return fieldPath;
  if (fieldPath.startsWith('requirements') && handlers.has('requirements')) return 'requirements';
  return null;
}

/** 各业务页按 fieldPath 注册 AI 片段改写落点（支持多页并存，避免互相覆盖） */
export const useAiPageContentApplyStore = defineStore('aiPageContentApply', () => {
  const applyHandlers = ref(new Map<string, ApplyHandler>());
  const locateHandlers = ref(new Map<string, LocateHandler>());

  function registerApplyHandler(fieldPath: string, fn: ApplyHandler) {
    const next = new Map(applyHandlers.value);
    next.set(fieldPath, fn);
    applyHandlers.value = next;
  }

  function unregisterApplyHandler(fieldPath: string) {
    if (!applyHandlers.value.has(fieldPath)) return;
    const next = new Map(applyHandlers.value);
    next.delete(fieldPath);
    applyHandlers.value = next;
  }

  function clearApplyHandler() {
    applyHandlers.value = new Map();
  }

  function registerLocateHandler(fieldPath: string, fn: LocateHandler) {
    const next = new Map(locateHandlers.value);
    next.set(fieldPath, fn);
    locateHandlers.value = next;
  }

  function unregisterLocateHandler(fieldPath: string) {
    if (!locateHandlers.value.has(fieldPath)) return;
    const next = new Map(locateHandlers.value);
    next.delete(fieldPath);
    locateHandlers.value = next;
  }

  function clearLocateHandler() {
    locateHandlers.value = new Map();
  }

  function applyFieldReplace(args: AiPageContentApplyArgs): boolean {
    const key = resolveHandlerKey(args.fieldPath, applyHandlers.value as Map<string, unknown>);
    if (!key) return false;
    const fn = applyHandlers.value.get(key);
    return fn ? fn(args) : false;
  }

  function locateFieldQuote(
    quoted: string,
    fieldPath: string,
    options?: AiPageContentLocateOptions,
  ): boolean {
    const key = resolveHandlerKey(fieldPath, locateHandlers.value as Map<string, unknown>);
    if (!key) return false;
    const fn = locateHandlers.value.get(key);
    return fn ? fn(quoted, fieldPath, options) : false;
  }

  return {
    registerApplyHandler,
    unregisterApplyHandler,
    clearApplyHandler,
    registerLocateHandler,
    unregisterLocateHandler,
    clearLocateHandler,
    applyFieldReplace,
    locateFieldQuote,
  };
});
